import { Card, Prose } from '../ui/components';

function Code({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-xl border border-white/8 bg-black/40 p-3 font-mono text-xs leading-5 text-zinc-300">
      {children}
    </pre>
  );
}

function QA({ q, a, open = false }: { q: string; a: string; open?: boolean }) {
  return (
    <details open={open} className="rounded-2xl border border-white/6 bg-black/15 px-5 py-4">
      <summary className="cursor-pointer list-none text-base font-medium text-zinc-100">{q}</summary>
      <p className="mt-3 text-sm leading-6 text-zinc-400">{a}</p>
    </details>
  );
}

export function Docs() {
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h1 className="text-3xl font-semibold tracking-tight text-white">Documentation</h1>
        <p className="max-w-4xl text-sm leading-6 text-zinc-400">
          How to read the scanner, how the signals are grouped, and how to drive it programmatically.
        </p>
      </div>

      <Card title="How to read the results">
        <Prose>
          <p>
            The most useful distinction is between <strong className="text-zinc-200">claimed
            identity</strong> and <strong className="text-zinc-200">observed properties</strong>.
            The User-Agent and Client Hints describe what the browser says it is; the GPU renderer,
            screen metrics, font shaping, codec pipeline, and engine timing describe what it
            actually is. Neither is suspicious alone. A mismatch between them is the signal.
          </p>
          <p>
            Read a scan in this order: first, direct automation artifacts (the{' '}
            <code className="font-mono text-zinc-300">webdriver</code> flag, leaked framework
            globals, CDP stack frames); second, inconsistencies between the claimed identity and
            observed properties; third, only then combinations of weaker anomalies.
          </p>
          <p>
            A single unusual property — an odd screen size, an unfamiliar GPU, an unusual core
            count — is not evidence of automation. Several independent properties pointing the
            same way are.
          </p>
        </Prose>
      </Card>

      <Card title="Signal status values">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-zinc-500">
                <th className="py-2 pr-4 font-medium">status</th>
                <th className="py-2 font-medium">meaning</th>
              </tr>
            </thead>
            <tbody className="text-zinc-400">
              <tr className="border-b border-white/5">
                <td className="py-2 pr-4 font-mono text-xs text-emerald-300">ok</td>
                <td className="py-2">The probe ran and produced a value.</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-2 pr-4 font-mono text-xs text-zinc-400">unavailable</td>
                <td className="py-2">The API or resource was not reachable (e.g. the edge Worker is not deployed).</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-2 pr-4 font-mono text-xs text-amber-300">denied</td>
                <td className="py-2">You (or the browser) declined a permission. Different from "unavailable".</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-2 pr-4 font-mono text-xs text-sky-300">suspended</td>
                <td className="py-2">The browser stalled the API for policy reasons (e.g. the AudioContext stayed suspended under the autoplay policy), so nothing was measured.</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-2 pr-4 font-mono text-xs text-zinc-400">unsupported</td>
                <td className="py-2">This browser does not implement the API at all.</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-2 pr-4 font-mono text-xs text-rose-300">error</td>
                <td className="py-2">The probe threw. It is contained; other probes are unaffected.</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-2 pr-4 font-mono text-xs text-amber-300">timeout</td>
                <td className="py-2">The probe exceeded its time budget and was abandoned.</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs text-zinc-500">skipped</td>
                <td className="py-2">The probe needs a user gesture or permission you did not grant.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="The bot score">
        <Prose>
          <p>
            The score is a 0–100 summary across four layers: hardware/OS, browser, network/proxy,
            and behaviour. Each layer reports a level (low/medium/high) and the points it
            contributed. Findings marked low-confidence are shown but contribute zero, so
            speculative heuristics cannot manufacture a high score.
          </p>
          <p>
            The individual rules and their thresholds are deliberately not exposed, so the output
            stays useful for debugging without reading like a bypass guide. A score is a summary of
            evidence, not a verdict — privacy tools, hardened browsers, VMs, and unusual setups can
            legitimately trigger it.
          </p>
        </Prose>
      </Card>

      <Card title="Cross-context comparison">
        <Prose>
          <p>
            The same navigator/device values are read in three execution contexts: the main page, a
            same-origin iframe, and a worker. A genuine browser reports identical values in all
            three. Automation frameworks and anti-detect browsers frequently patch only the main
            context, so a divergence is strong evidence of partial spoofing. Divergences are
            reported per signal and feed the browser layer of the score.
          </p>
        </Prose>
      </Card>

      <Card title="Programmatic access" subtitle="window.FP_SCAN (also aliased as window.FINGERPRINT_SCAN)">
        <Prose>
          <p>
            Once collection completes, the full result is available as a stable object, so scripts
            and agents can read it without parsing the UI.
          </p>
        </Prose>
        <Code>{`await new Promise(r => {
  const t = setInterval(() => {
    if (window.FP_SCAN?.status?.startsWith('COLLECT')) {
      clearInterval(t); r();
    }
  }, 200);
});
const scan = window.FP_SCAN;
console.log(scan.fingerprintHash, scan.botScore, scan.fingerprintParts);`}</Code>
        <Prose>
          <p>
            The object exposes the status, fingerprint hash and id, fingerprint parts, the bot
            score and per-layer reasons, all signals, and the findings (without rule thresholds).
            It also provides <code className="font-mono text-zinc-300">rerun()</code>,{' '}
            <code className="font-mono text-zinc-300">exportJson()</code>, and{' '}
            <code className="font-mono text-zinc-300">compareTo(previous)</code>.
          </p>
        </Prose>
      </Card>

      <Card
        title="JSON endpoints"
        subtitle="The machine-readable surface, for curl / wget / scripts"
      >
        <Prose>
          <p>
            Two endpoints serve JSON without a browser. <code className="font-mono text-zinc-300">/json</code>{' '}
            describes <em>your own request</em> at the transport layer;{' '}
            <code className="font-mono text-zinc-300">/catalog.json</code> is the full technique
            catalog.
          </p>
        </Prose>
        <Code>{`curl https://fingerprint-labs.pages.dev/json
curl https://fingerprint-labs.pages.dev/catalog.json | jq '.summary.byStatus'`}</Code>
        <Prose>
          <p>
            <code className="font-mono text-zinc-300">/json</code> reports TLS version and cipher,
            ClientHello length, the ClientHello cipher/extension hashes, HTTP and QUIC version, TCP
            RTT, and the network the request arrived on. None of that needs JavaScript.
          </p>
          <p className="text-amber-200">
            It does <strong>not</strong> report a browser fingerprint, and says so:{' '}
            <code className="font-mono">browser.available</code> is{' '}
            <code className="font-mono">false</code> with a reason. Canvas, WebGL, audio, fonts and
            timing can only be measured by executing JavaScript, and curl does not execute
            JavaScript. No static host can return your browser fingerprint to a script, and
            inventing one would be worthless.
          </p>
          <p>
            For the browser half use the <strong>Export JSON</strong> button on the scan page, or
            the headless CLI, which drives a real browser and fills in both halves:
          </p>
        </Prose>
        <Code>{`npm run export:json -- --url https://fingerprint-labs.pages.dev
npm run export:json -- --url https://fingerprint-labs.pages.dev --out scan.json
npm run export:json -- --url https://fingerprint-labs.pages.dev --pretty=false | jq '.browser.collected.botScore'`}</Code>
      </Card>

      <Card title="Query flags">
        <Code>{`?nomatch=1   disable fingerprint-ID persistence (useful when testing spoofed browsers)
?rerun=1     reserved for forcing a fresh collection`}</Code>
      </Card>

      <Card title="Frequently asked questions">
        <div className="space-y-3">
          <QA
            q="Is the fingerprint unique?"
            a="No. Fingerprinting is not 100% unique. Many users share a fingerprint, especially on common hardware — iPhones and iPads of the same model are near-identical."
            open
          />
          <QA
            q="Can fingerprinting lies be detected?"
            a="Often, yes. If a User-Agent claims Firefox but the engine timing, GPU renderer, and font shaper say Chromium, that contradiction is detectable. This is the core idea behind the coherence checks."
            open
          />
          <QA
            q="Why is JA3/JA4 not computed directly?"
            a="Because a static page cannot see its own TLS ClientHello. Cloudflare's free tier exposes only SHA-1 hashes of the cipher and extension lists, so the edge card does a signature lookup instead. A true JA4 requires the paid Bot Management add-on."
            open
          />
          <QA
            q="Does visiting this site or interacting with it track me?"
            a="No. The scan sets only a single experimental fingerprint ID in local storage, which you can disable with ?nomatch=1. There is no analytics, no account, and no server-side record. The scan contacts three public STUN servers, two public DNS resolvers, and your own loopback interface (your browser may ask for Local Network Access first); none of that is stored or submitted anywhere, and the loopback and resolver results never enter the fingerprint or the score."
          />
          <QA
            q="Is this a production bot-detection verdict?"
            a="No. It is a simplified research playground. Production systems combine fingerprints with network reputation, historical behaviour, account context, and much more server-side signal."
          />
        </div>
      </Card>
    </div>
  );
}
